import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { newsList, formatDate } from "@/lib/media";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 4;
const TABS = ["全部", "定档", "资讯", "幕后", "榜单"];

export default function NewsListPage() {
  const [keyword, setKeyword] = useState("");
  const [tab, setTab] = useState("全部");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "影视动态与新闻 - 华丽的外出影视大全",
    description:
      "华丽的外出影视动态频道，汇总影视定档消息、幕后花絮、行业观察与年度榜单，帮助你跟上正在发生的影视事件。",
    keywords: "影视动态,影视新闻,定档消息,幕后花絮,行业观察,华丽的外出",
  });

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [keyword, tab, page]);

  const filtered = useMemo(() => {
    const word = keyword.trim().toLowerCase();
    return newsList
      .filter((item) => (tab === "全部" ? true : item.category === tab))
      .filter((item) =>
        word
          ? `${item.title} ${item.excerpt} ${item.author}`.toLowerCase().includes(word)
          : true,
      )
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [keyword, tab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const items = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [keyword, tab]);

  const reset = () => {
    setKeyword("");
    setTab("全部");
  };

  return (
    <SiteLayout>
      <div className="shell pt-5 md:pt-7">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <span className="text-foreground-800">影视动态</span>
        </nav>

        <header className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-primary-950 px-5 py-7 md:px-8 md:py-9">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-1 rounded-full bg-accent-500" />
                <span className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-300">
                  Newsroom
                </span>
              </div>
              <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-background-50 md:text-3xl">
                影视动态
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-300">
                定档、幕后、行业观察与榜单。我们只写有信息量的那部分，不写通稿。
              </p>
            </div>
            <div className="w-full max-w-sm">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
                  <i className="ri-search-line text-base text-background-400" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索新闻标题、摘要或作者"
                  aria-label="搜索影视新闻"
                  className="h-10 w-full rounded-md border border-background-50/20 bg-background-50/10 pl-9 pr-3 text-sm text-background-50 placeholder:text-background-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                />
              </div>
              <p className="mt-2 text-[11px] text-background-400">
                共 {newsList.length} 篇动态，输入关键词即时筛选
              </p>
            </div>
          </div>
        </header>

        <div className="mt-5 flex flex-wrap items-center gap-1.5">
          {TABS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTab(item)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                tab === item
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 sm:flex-row"
                >
                  <div className="skeleton h-32 w-full shrink-0 rounded-md sm:h-24 sm:w-40" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="skeleton h-4 w-2/3 rounded" />
                    <div className="skeleton h-3 w-full rounded" />
                    <div className="skeleton h-3 w-1/2 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon="ri-newspaper-line"
              title="没有找到相关动态"
              desc="换个关键词试试看，或者清除筛选回到全部新闻。"
              action={
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line text-sm" />
                  清除筛选
                </button>
              }
              actionTo="/news"
              actionLabel="新闻首页"
            />
          ) : (
            <>
              <div className="space-y-3">
                {items.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex cursor-pointer flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/30 sm:flex-row"
                  >
                    <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-secondary-100 sm:h-28 sm:w-44">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} 新闻配图`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                        <span className="rounded-md bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                          {item.category}
                        </span>
                        <span>{formatDate(item.date)}</span>
                        <span>{item.author}</span>
                        <span className="hidden sm:inline">{item.readTime}</span>
                      </div>
                      <h2 className="mt-2 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </h2>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {item.excerpt}
                      </p>
                      <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-primary-700">
                        阅读全文
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onChange={(next) => {
                  setPage(next);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}